import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { customSizes, palette, spaces } from '../../../../assets/styles/themes/tokens';
import { Box, Stack, Typography } from '@mui/material';
import AIAssistantChatHistoryList from './AIAssistantChatHistoryList';
import {
  getConversationsListPagination,
  getConversationsListResults,
  getConversationsLoadingState
} from '../../../../reducers/aiAssistant.selectors';
import { useSelector } from 'react-redux';
import useAction from '../../../../utils/useAction';
import {
  getConversationsPaginatedList as getConversationsPaginatedListAction
} from '../../../../actions/aiAssistant.actions';
import { AiLoadingStatus } from '../../../../reducers/aiAssistant.reducers';
import AIAssistantChatHistoryLoader from './AIAssistantChatHistoryLoader';
import InfiniteScroll from 'react-infinite-scroller';
import { hasNextConversationListPage } from './utils';
import useScreenSize from '../../../../utils/useScreenSize';
import useRedirectIfNoAIAssistantAccess from '../../../../utils/useRedirectIfNoAIAssistantAccess';
import { MasterLayoutContext } from '../../../../state/MasterLayoutProvider';

const getHistoryPageContainerStyles = (isSmallScreen: boolean) => ({
  maxWidth: isSmallScreen ? '100%' : customSizes.aiAssistantForm.containerMaxWidth,
  width: '100%',
  padding: isSmallScreen ? 0 : `${spaces.md} ${spaces.xxxl} ${spaces.xl} ${spaces.xxxl}`,
  alignItems: 'center',
  gap: spaces.lg,
  alignSelf: 'stretch',
  margin: '0 auto',
});

const AIAssistantChatHistory = () => {
  const { t } = useTranslation(); 
  const { mainContentContainer } = useContext(MasterLayoutContext);
  const loadingState = useSelector(getConversationsLoadingState);
  const conversations = useSelector(getConversationsListResults);
  const pagination = useSelector(getConversationsListPagination);
  const { isLoading: isUserLoading, hasAccess } = useRedirectIfNoAIAssistantAccess();
  const getConversationsPaginatedList = useAction(getConversationsPaginatedListAction);
  const { isXsScreen, isSmScreen } = useScreenSize();
  const isSmallScreen = isXsScreen || isSmScreen;

  useEffect(() => {
    if (loadingState === AiLoadingStatus.NONE) {
      getConversationsPaginatedList();
    }
  }, [loadingState, getConversationsPaginatedList]);

  const loadMore = () => {
    getConversationsPaginatedList();
  };

  const isLoading = [
    AiLoadingStatus.INITIAL, 
    AiLoadingStatus.NONE
  ].includes(loadingState);
  
  if (isUserLoading || !hasAccess) {
    return null;
  }
  
  return (
    <Box sx={getHistoryPageContainerStyles(isSmallScreen)}>
      {isLoading ? (
        <AIAssistantChatHistoryLoader />
      ) : (
        <>
          {conversations.length > 0 ? (
            <InfiniteScroll
              loadMore={loadMore}
              hasMore={hasNextConversationListPage(pagination)}
              initialLoad={false}
              useWindow={false}
              getScrollParent={() => mainContentContainer || null}
            >
              <AIAssistantChatHistoryList items={conversations} />
            </InfiniteScroll>
          ) : (
            <Stack spacing={spaces.xs} sx={{ padding: spaces.md }}>
              <Typography sx={{ color: palette.text.secondary }} variant='h6'>
                {t('You haven\'t had any conversations yet')}
              </Typography>
              <Typography sx={{ color: palette.text.secondary }} variant='body2'>
                {t('Once you start a chat, it will be displayed here')}
              </Typography>
            </Stack>
          )}
          {loadingState === AiLoadingStatus.PAGINATION && (
            <AIAssistantChatHistoryLoader />
          )}
        </>
      )}
    </Box>
  );
};

export default AIAssistantChatHistory;
